import { Badge, Container, Grid, Loader, Paper, Table, Text, Title } from "@mantine/core"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"

import { PageHeader } from "@/components/PageHeader"
import { queries } from "@/lib/queries"

const PLAYFAIR = "'Playfair Display', Georgia, serif"

export const Route = createFileRoute("/companies/$id/milestones")({
  component: CompanyMilestones,
})

function CompanyMilestones() {
  const { id } = Route.useParams()
  const { data, isPending, isError } = useQuery(queries.companyMilestones(id))

  if (isPending) return <Loader />
  if (isError) return <Text c="var(--pitch-negative)">Failed to load milestones</Text>

  return (
    <Container size="xl">
      <PageHeader eyebrow="Milestones">{id.toUpperCase()}</PageHeader>

      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper withBorder p="md" radius={0} style={{ borderColor: "var(--pitch-border)" }}>
            <Title
              order={4}
              ff={PLAYFAIR}
              fw={500}
              c="var(--pitch-text-primary)"
              mb="sm"
              style={{ letterSpacing: "-0.005em" }}
            >
              Quarterly Timeline
            </Title>
            {data.timeline.length === 0 ? (
              <Text c="var(--pitch-text-tertiary)" size="sm">
                No timeline data
              </Text>
            ) : (
              <Table striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Quarter</Table.Th>
                    <Table.Th>Code</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.timeline.map((t) => (
                    <Table.Tr key={t.quarter}>
                      <Table.Td>{t.quarter}</Table.Td>
                      <Table.Td>
                        <Badge size="sm" variant="light">
                          {t.milestoneCode}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper withBorder p="md" radius={0} style={{ borderColor: "var(--pitch-border)" }}>
            <Title
              order={4}
              ff={PLAYFAIR}
              fw={500}
              c="var(--pitch-text-primary)"
              mb="sm"
              style={{ letterSpacing: "-0.005em" }}
            >
              Milestone Details
            </Title>
            {data.details.length === 0 ? (
              <Text c="var(--pitch-text-tertiary)" size="sm">
                No detail data
              </Text>
            ) : (
              <Table striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Milestone</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Planned</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.details.map((d) => (
                    <Table.Tr key={d.milestoneName}>
                      <Table.Td>
                        <Text fw={600} size="sm" c="var(--pitch-text-primary)">
                          {d.milestoneName}
                        </Text>
                        {d.notes.fold(
                          () => null,
                          (notes) => (
                            <Text size="xs" c="var(--pitch-text-tertiary)" mt={2}>
                              {notes}
                            </Text>
                          ),
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          size="xs"
                          color={
                            d.status === "completed"
                              ? "pitchPositive"
                              : d.status === "on_track"
                                ? "pitchAccent"
                                : d.status === "at_risk"
                                  ? "pitchInflection"
                                  : "gray"
                          }
                        >
                          {d.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {d.plannedDate.fold(
                          () => "—",
                          (p) => new Date(p).toLocaleDateString(),
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>
        </Grid.Col>
      </Grid>
    </Container>
  )
}
